import type { BuildingDef, BuildingLevel } from "./schema/building.js";
import { buildingCost, buildingTimeSec, powerConsumption, hqTimeSec } from "./data/curves.js";
import { BUILDINGS } from "./data/buildings.js";
import { MAX_LEVEL } from "./schema/common.js";

const byId = new Map<string, BuildingDef>(BUILDINGS.map((b) => [b.id, b]));
const levelCache = new Map<string, BuildingLevel[]>();

export function buildingById(id: string): BuildingDef {
  const b = byId.get(id);
  if (!b) throw new Error(`unknown building ${id}`);
  return b;
}

export function hasBuilding(id: string): boolean {
  return byId.has(id);
}

export const UPGRADEABLE_BUILDING_IDS: readonly string[] = BUILDINGS.filter((b) => b.upgradeable).map((b) => b.id);

function attributeAt(curve: BuildingDef["attributes"][string], level: number): number {
  if ("perLevel" in curve) return curve.perLevel[level - 1] ?? 0;
  const v = curve.base * Math.pow(curve.growth, level - 1);
  return curve.round === "none" ? v : Math.round(v);
}

export function buildingLevels(id: string): BuildingLevel[] {
  const cached = levelCache.get(id);
  if (cached) return cached;
  const def = buildingById(id);
  if (!def.upgradeable) throw new Error(`${id} has no levels`);
  const isHq = def.id === "bld.command_nexus";
  const levels: BuildingLevel[] = [];
  for (let level = 1; level <= MAX_LEVEL; level++) {
    const attributes: Record<string, number> = {};
    for (const [key, curve] of Object.entries(def.attributes)) attributes[key] = attributeAt(curve, level);
    levels.push({
      level,
      cost: level === 1 ? { RAT: 0, POL: 0, ALY: 0, ENC: 0 } : buildingCost(def, level),
      timeSec: level === 1 ? 0 : isHq ? hqTimeSec(level) : buildingTimeSec(def, level),
      power: def.id === "bld.fusion_reactor" ? 0 : powerConsumption(level),
      attributes,
    });
  }
  levelCache.set(id, levels);
  return levels;
}

export function buildingLevel(id: string, level: number): BuildingLevel {
  const row = buildingLevels(id)[level - 1];
  if (!row) throw new RangeError(`${id} has no level ${level}`);
  return row;
}

/** Level-1 build (placing a new copy) costs the same as upgrading to level 2 halved, with a short timer. */
export function buildingPlacementCost(id: string): { cost: BuildingLevel["cost"]; timeSec: number } {
  const def = buildingById(id);
  const l2 = buildingCost(def, 2);
  return {
    cost: { RAT: Math.round(l2.RAT / 2), POL: Math.round(l2.POL / 2), ALY: 0, ENC: 0 },
    timeSec: Math.max(5, Math.round(buildingTimeSec(def, 2) / 2)),
  };
}
